import React, { useState, useMemo, useDeferredValue, useEffect } from "react";

const PAGE_SIZE = 60;
import {
  Search,
  Filter,
  X,
  Layers,
  Trash2,
  PlusCircle,
  Calculator,
  Eye,
  TrendingUp,
  Copy,
  Percent,
  ChevronDown,
  FileSpreadsheet,
  FileText,
  ArrowUpDown,
  Edit,
  Save,
  Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import ImageThumbnail from "@/components/common/ImageThumbnail";
import { formatCurrency } from "@/utils/formatters";
import { toast } from "sonner";
import api from "@/lib/axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * QuoteTypesTable - Main price index table component
 * Displays all indexed quote types with filtering, sorting, and actions
 */
const QuoteTypesTable = ({ 
  quoteTypes, 
  onDelete, 
  onDeleteMultiple, 
  onViewDetail, 
  onSelectForCalculator, 
  onAddQuote, 
  onViewTrend, 
  onDuplicate, 
  onPriceAdjust, 
  onOpenHistory, 
  selectedTypeIds, 
  onSelectionChange, 
  materials, 
  aluminumSeries, 
  onExport, 
  onImagePreview, 
  availableFactories = [],
  projectFilter = "all",
  onProjectFilterChange,
  allProjects = [],
  glassFilter = "all",
  onGlassFilterChange,
  availableGlassSpecs = [],
  extrasFilter = "all",
  onExtrasFilterChange,
  availableExtrasSpecs = [],
  onRefresh // Callback to refresh data after edit
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterMaterial, setFilterMaterial] = useState("all");
  const [filterSeries, setFilterSeries] = useState("all");
  const [filterColor, setFilterColor] = useState("all");
  const [filterFactory, setFilterFactory] = useState("all");
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [selectedIds, setSelectedIds] = useState(new Set());
  
  // Edit modal state
  const [editingType, setEditingType] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    name: "",
    material: "",
    color: "",
    series: "",
    glass_spec: "",
    extra_specs: "",
    opening_design: "",
    factory_name: ""
  });
  const [isSaving, setIsSaving] = useState(false);

  // Open edit modal with type data
  const handleOpenEdit = (type) => {
    setEditingType(type);
    setEditForm({
      name: type.name || "",
      material: type.material || "",
      color: type.color || "",
      series: type.series || "",
      glass_spec: type.glass_spec || "",
      extra_specs: type.extra_specs || "",
      opening_design: type.opening_design || "",
      factory_name: type.factory_name || ""
    });
    setShowEditModal(true);
  };

  // Save edited type
  const handleSaveEdit = async () => {
    if (!editingType) return;
    
    setIsSaving(true);
    try {
      await api.put(`${BACKEND_URL}/api/quote-types/${editingType.id}`, editForm);
      toast.success(`Tipo "${editForm.name}" actualizado correctamente`);
      setShowEditModal(false);
      setEditingType(null);
      
      // Refresh data
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error("Error updating type:", error);
      toast.error(error.response?.data?.detail || "Error al actualizar el tipo");
    } finally {
      setIsSaving(false);
    }
  };

  // Filter types - deferred search keeps typing responsive; memoized so unrelated re-renders skip the work
  const deferredSearch = useDeferredValue(searchTerm);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  useEffect(() => { setVisibleCount(PAGE_SIZE); }, [deferredSearch, filterMaterial, filterSeries, filterColor, filterFactory, sortConfig, projectFilter, glassFilter, extrasFilter]);
  const filteredTypes = useMemo(() => quoteTypes
    .filter(type => {
      // Basic filters
      const term = deferredSearch.toLowerCase();
      const matchesSearch = type.name.toLowerCase().includes(term) ||
        type.glass_spec?.toLowerCase().includes(term) ||
        type.extra_specs?.toLowerCase().includes(term);
      const matchesMaterial = filterMaterial === "all" || type.material === filterMaterial;
      const matchesSeries = filterSeries === "all" || type.series === filterSeries;
      const matchesColor = filterColor === "all" || type.color === filterColor;
      const matchesFactory = filterFactory === "all" || type.factory_name === filterFactory;
      
      // Project filter - check if any entry has the specified project
      const matchesProject = projectFilter === "all" || 
        (type.entries && type.entries.some(e => e.project_name === projectFilter));
      
      // Glass and extras filters
      const matchesGlass = !glassFilter || glassFilter === "all" || type.glass_spec === glassFilter;
      const matchesExtras = !extrasFilter || extrasFilter === "all" || type.extra_specs === extrasFilter;
      
      return matchesSearch && matchesMaterial && matchesSeries && matchesColor && matchesFactory && matchesProject && matchesGlass && matchesExtras;
    })
    .sort((a, b) => {
      if (sortConfig.key === 'name') {
        return sortConfig.direction === 'asc' 
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
      }
      return sortConfig.direction === 'asc'
        ? a[sortConfig.key] - b[sortConfig.key]
        : b[sortConfig.key] - a[sortConfig.key];
    }), [quoteTypes, deferredSearch, filterMaterial, filterSeries, filterColor, filterFactory, sortConfig, projectFilter, glassFilter, extrasFilter]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Get unique series and colors from current data
  const availableSeries = [...new Set(quoteTypes.filter(t => t.series).map(t => t.series))];
  const availableColors = [...new Set(quoteTypes.filter(t => t.color).map(t => t.color))];

  // Selection handlers - sync with parent
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      if (onSelectionChange) {
        onSelectionChange(Array.from(newSet));
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    let newSet;
    if (selectedIds.size === filteredTypes.length) {
      newSet = new Set();
    } else {
      newSet = new Set(filteredTypes.map(t => t.id));
    }
    setSelectedIds(newSet);
    if (onSelectionChange) {
      onSelectionChange(Array.from(newSet));
    }
  };

  const handleDeleteSelected = () => {
    if (selectedIds.size === 0) return;
    onDeleteMultiple(Array.from(selectedIds));
    setSelectedIds(new Set());
    if (onSelectionChange) {
      onSelectionChange([]);
    }
  };

  return (
    <Card className="metric-card">
      <CardHeader className="border-b border-slate-100 p-4">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <CardTitle className="text-lg font-medium text-slate-900 flex items-center gap-2">
                <Layers className="w-5 h-5" />
                Índice de Precios
              </CardTitle>
              <CardDescription className="text-slate-500">
                {quoteTypes.length} tipos de elementos indexados
              </CardDescription>
            </div>
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                data-testid="search-input"
                placeholder="Buscar tipo..."
                className="search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          
          {/* Filters Row */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-400" />
            </div>
            <div className="flex items-center gap-1">
              <span className="text-xs text-slate-400 uppercase">Material:</span>
              <Select value={filterMaterial} onValueChange={setFilterMaterial}>
                <SelectTrigger data-testid="filter-material" className="w-[120px] h-8 text-sm">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {materials.map((mat) => (
                    <SelectItem key={mat} value={mat}>{mat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-xs text-slate-400 uppercase">Serie:</span>
              <Select value={filterSeries} onValueChange={setFilterSeries}>
                <SelectTrigger data-testid="filter-series" className="w-[120px] h-8 text-sm">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {availableSeries.map((ser) => (
                    <SelectItem key={ser} value={ser}>{ser}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-xs text-slate-400 uppercase">Color:</span>
              <Select value={filterColor} onValueChange={setFilterColor}>
                <SelectTrigger data-testid="filter-color" className="w-[120px] h-8 text-sm">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {availableColors.map((color) => (
                    <SelectItem key={color} value={color}>{color}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-xs text-slate-400 uppercase">Fábrica:</span>
              <Select value={filterFactory} onValueChange={setFilterFactory}>
                <SelectTrigger data-testid="filter-factory" className="w-[130px] h-8 text-sm">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {availableFactories.map((factory) => (
                    <SelectItem key={factory} value={factory}>{factory}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Project Filter (NEW) */}
            {allProjects.length > 0 && (
              <div className="flex items-center gap-1">
                <span className="text-xs text-slate-400 uppercase">Proyecto:</span>
                <Select 
                  value={projectFilter} 
                  onValueChange={onProjectFilterChange}
                >
                  <SelectTrigger data-testid="filter-project" className="w-[150px] h-8 text-sm">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los proyectos</SelectItem>
                    {allProjects.map((project) => (
                      <SelectItem key={project} value={project}>{project}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {/* Glass Filter (NEW) */}
            {availableGlassSpecs.length > 0 && (
              <div className="flex items-center gap-1">
                <span className="text-xs text-slate-400 uppercase">Vidrio:</span>
                <Select 
                  value={glassFilter} 
                  onValueChange={onGlassFilterChange}
                >
                  <SelectTrigger data-testid="filter-glass-sales" className="w-[140px] h-8 text-sm">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todo Vidrio</SelectItem>
                    {availableGlassSpecs.map((glass) => (
                      <SelectItem key={glass} value={glass}>{glass}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {/* Extras/Varios Filter (NEW) */}
            {availableExtrasSpecs.length > 0 && (
              <div className="flex items-center gap-1">
                <span className="text-xs text-slate-400 uppercase">Varios:</span>
                <Select 
                  value={extrasFilter} 
                  onValueChange={onExtrasFilterChange}
                >
                  <SelectTrigger data-testid="filter-extras-sales" className="w-[140px] h-8 text-sm">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {availableExtrasSpecs.map((extra) => (
                      <SelectItem key={extra} value={extra}>{extra}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {(filterMaterial !== "all" || filterSeries !== "all" || filterColor !== "all" || filterFactory !== "all" || projectFilter !== "all" || (glassFilter && glassFilter !== "all") || (extrasFilter && extrasFilter !== "all")) && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => { 
                  setFilterMaterial("all"); 
                  setFilterSeries("all"); 
                  setFilterColor("all"); 
                  setFilterFactory("all");
                  if (onProjectFilterChange) onProjectFilterChange("all");
                  if (onGlassFilterChange) onGlassFilterChange("all");
                  if (onExtrasFilterChange) onExtrasFilterChange("all");
                }}
                className="text-slate-500 h-8"
              >
                <X className="w-4 h-4 mr-1" /> Limpiar
              </Button>
            )}
            
            <div className="ml-auto flex items-center gap-2">
              {/* Price adjustment dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="h-8"
                    data-testid="price-adjust-btn"
                  >
                    <Percent className="w-4 h-4 mr-1" /> 
                    Ajustar Precios
                    <ChevronDown className="w-3 h-3 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={onPriceAdjust} data-testid="price-adjust-new-btn">
                    <Percent className="w-4 h-4 mr-2" />
                    Nuevo Ajuste
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={onOpenHistory} data-testid="price-adjust-history-btn">
                    <TrendingUp className="w-4 h-4 mr-2" />
                    Ver Historial de Ajustes
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
              {/* Export dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="h-8"
                    data-testid="export-btn"
                  >
                    <FileSpreadsheet className="w-4 h-4 mr-1" /> 
                    Exportar
                    <ChevronDown className="w-3 h-3 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => onExport(filteredTypes, 'csv')} data-testid="export-csv-btn">
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    Exportar a CSV
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onExport(filteredTypes, 'pdf')} data-testid="export-pdf-btn">
                    <FileText className="w-4 h-4 mr-2" />
                    Exportar a PDF con Imágenes
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
              {/* Delete selected button */}
              {selectedIds.size > 0 && (
                <Button 
                  variant="destructive" 
                  size="sm"
                  onClick={handleDeleteSelected}
                  className="h-8"
                  data-testid="delete-selected-btn"
                >
                  <Trash2 className="w-4 h-4 mr-1" /> 
                  Eliminar {selectedIds.size}
                </Button>
              )}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {filteredTypes.length === 0 ? (
          <div className="empty-state">
            <Search className="empty-state-icon" />
            <p className="text-slate-600 font-medium">No se encontraron resultados</p>
            <p className="text-slate-400 text-sm mt-1">
              {quoteTypes.length === 0 
                ? "Carga un archivo Excel para comenzar" 
                : "Intenta con otro término de búsqueda o filtro"
              }
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table className="industrial-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40px]">
                    <input
                      type="checkbox"
                      checked={selectedIds.size === filteredTypes.length && filteredTypes.length > 0}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 rounded border-slate-300 cursor-pointer"
                      data-testid="select-all-checkbox"
                    />
                  </TableHead>
                  <TableHead className="w-[70px]">Imagen</TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-slate-100"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center gap-1">
                      Tipo
                      <ArrowUpDown className="w-3 h-3" />
                    </div>
                  </TableHead>
                  <TableHead>Material</TableHead>
                  <TableHead>Color</TableHead>
                  <TableHead>Serie</TableHead>
                  <TableHead 
                    className="text-right cursor-pointer hover:bg-slate-100"
                    onClick={() => handleSort('entries_count')}
                  >
                    <div className="flex items-center justify-end gap-1">
                      Muestras
                      <ArrowUpDown className="w-3 h-3" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="text-right cursor-pointer hover:bg-slate-100"
                    onClick={() => handleSort('average_cost_per_m2')}
                  >
                    <div className="flex items-center justify-end gap-1">
                      Promedio/m²
                      <ArrowUpDown className="w-3 h-3" />
                    </div>
                  </TableHead>
                  <TableHead className="text-right">Rango</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTypes.slice(0, visibleCount).map((type) => (
                  <TableRow key={type.id} className={`table-row-hover ${selectedIds.has(type.id) ? 'bg-blue-50' : ''}`}>
                    <TableCell>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(type.id)}
                        onChange={() => toggleSelect(type.id)}
                        className="w-4 h-4 rounded border-slate-300 cursor-pointer"
                        data-testid={`select-${type.id}`}
                      />
                    </TableCell>
                    <TableCell>
                      <ImageThumbnail
                        src={type.image_data || (type.image_url ? `${BACKEND_URL}${type.image_url}` : null)}
                        alt={type.name}
                        className="w-12 h-12 rounded border border-slate-200"
                        onClick={() => (type.image_data || type.image_url) && onImagePreview(type.image_data || `${BACKEND_URL}${type.image_url}`, type.name)}
                      />
                    </TableCell>
                    <TableCell className="font-medium text-slate-900">
                      {type.name}
                    </TableCell>
                    <TableCell>
                      <Badge variant={type.material === "Aluminio" ? "default" : "secondary"}>
                        {type.material}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-600">
                      {type.color || "—"}
                    </TableCell>
                    <TableCell className="text-slate-500">
                      {type.series || "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge variant="secondary">{type.entries_count}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="font-mono font-bold text-slate-900">
                        {formatCurrency(type.average_cost_per_m2)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="text-sm text-slate-500">
                        {formatCurrency(type.min_cost_per_m2)} - {formatCurrency(type.max_cost_per_m2)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          data-testid={`use-calculator-${type.id}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => onSelectForCalculator(type)}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          title="Usar en calculadora"
                        >
                          <Calculator className="w-4 h-4" />
                        </Button>
                        <Button
                          data-testid={`add-quote-${type.id}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => onAddQuote(type)}
                          className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                          title="Agregar cotización"
                        >
                          <PlusCircle className="w-4 h-4" />
                        </Button>
                        <Button
                          data-testid={`view-detail-${type.id}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => onViewDetail(type)}
                          title="Ver detalle"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          data-testid={`view-trend-${type.id}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => onViewTrend(type)}
                          className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                          title="Ver tendencia de precios"
                        >
                          <TrendingUp className="w-4 h-4" />
                        </Button>
                        <Button
                          data-testid={`duplicate-type-${type.id}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => onDuplicate(type)}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          title="Duplicar tipo"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                        <Button
                          data-testid={`edit-type-${type.id}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenEdit(type)}
                          className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                          title="Editar información"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          data-testid={`delete-type-${type.id}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => onDelete(type.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {visibleCount < filteredTypes.length && (
              <div className="flex items-center justify-center gap-3 py-3 border-t text-sm text-slate-500">
                <span data-testid="types-visible-count">Mostrando {visibleCount} de {filteredTypes.length} tipos</span>
                <Button variant="outline" size="sm" onClick={() => setVisibleCount((c) => c + PAGE_SIZE * 2)} data-testid="types-show-more-btn">
                  Mostrar más
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setVisibleCount(filteredTypes.length)} data-testid="types-show-all-btn">
                  Mostrar todos
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>

      {/* Edit Type Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-amber-600" />
              Editar Tipo
            </DialogTitle>
            <DialogDescription>
              Modifica la información del tipo. Los cambios afectarán todas las cotizaciones asociadas.
            </DialogDescription>
          </DialogHeader>
          
          {editingType && (
            <div className="space-y-4 py-4">
              {/* Image preview */}
              <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                <ImageThumbnail
                  src={editingType.image_data || (editingType.image_url ? `${BACKEND_URL}${editingType.image_url}` : null)}
                  alt={editingType.name}
                  className="w-16 h-16 rounded border border-slate-200"
                />
                <div>
                  <p className="font-medium text-slate-900">{editingType.name}</p>
                  <p className="text-sm text-slate-500">{editingType.entries_count} cotizaciones • {formatCurrency(editingType.average_cost_per_m2)}/m²</p>
                </div>
              </div>

              {/* Edit form */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="edit-name">Descripción / Nombre</Label>
                  <Input
                    id="edit-name"
                    value={editForm.name}
                    onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Nombre del tipo"
                    data-testid="edit-type-name"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="edit-material">Material</Label>
                  <Select 
                    value={editForm.material} 
                    onValueChange={(value) => setEditForm(prev => ({ ...prev, material: value }))}
                  >
                    <SelectTrigger id="edit-material" data-testid="edit-type-material">
                      <SelectValue placeholder="Seleccionar material" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Aluminio">Aluminio</SelectItem>
                      <SelectItem value="uPVC">uPVC</SelectItem>
                      <SelectItem value="UPVC">UPVC</SelectItem>
                      <SelectItem value="Vidrio">Vidrio</SelectItem>
                      <SelectItem value="Madera">Madera</SelectItem>
                      <SelectItem value="Otro">Otro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="edit-color">Color</Label>
                  <Input
                    id="edit-color"
                    value={editForm.color}
                    onChange={(e) => setEditForm(prev => ({ ...prev, color: e.target.value }))}
                    placeholder="Ej: White, Black, Natural"
                    data-testid="edit-type-color"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="edit-series">Serie</Label>
                  <Input
                    id="edit-series"
                    value={editForm.series}
                    onChange={(e) => setEditForm(prev => ({ ...prev, series: e.target.value }))}
                    placeholder="Ej: 60, 70, 80"
                    data-testid="edit-type-series"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="edit-opening">Diseño de Apertura</Label>
                  <Input
                    id="edit-opening"
                    value={editForm.opening_design}
                    onChange={(e) => setEditForm(prev => ({ ...prev, opening_design: e.target.value }))}
                    placeholder="Ej: Corredizo, Batiente"
                    data-testid="edit-type-opening"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="edit-glass">Especificación de Vidrio</Label>
                  <Input
                    id="edit-glass"
                    value={editForm.glass_spec}
                    onChange={(e) => setEditForm(prev => ({ ...prev, glass_spec: e.target.value }))}
                    placeholder="Ej: 6mm, Doble Vidrio"
                    data-testid="edit-type-glass"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="edit-extras">Extras</Label>
                  <Input
                    id="edit-extras"
                    value={editForm.extra_specs}
                    onChange={(e) => setEditForm(prev => ({ ...prev, extra_specs: e.target.value }))}
                    placeholder="Especificaciones adicionales"
                    data-testid="edit-type-extras"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="edit-factory">Fábrica</Label>
                  <Input
                    id="edit-factory"
                    value={editForm.factory_name}
                    onChange={(e) => setEditForm(prev => ({ ...prev, factory_name: e.target.value }))}
                    placeholder="Nombre de la fábrica"
                    data-testid="edit-type-factory"
                  />
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)} disabled={isSaving}>
              Cancelar
            </Button>
            <Button onClick={handleSaveEdit} disabled={isSaving || !editForm.name.trim()}>
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Guardando...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Guardar Cambios
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default QuoteTypesTable;
